// Consentimientos §7216 / E-SIGN (VLT-286 · flujo 17 de docs/diagrams).
// Regla: sin consentimiento vigente para un uso, ese uso NO procede.
// El texto es versionado: cambiarlo exige re-consentir (fila nueva).
import { supabase, hasBackend } from '@/lib/supabase';
import { sessionUser } from '@/lib/sessionUser';

export type ConsentType = 'ai_processing' | 'external_translation' | 'analytics' | 'esign';

/** Versión vigente del texto. BORRADOR: el texto final lo fija el abogado
 *  (VLT-286); al ratificarlo se sube la versión y todos re-consienten. */
export const CONSENT_VERSION = '2026-08-27-draft1';

export interface ConsentStatus {
  granted: boolean;
  version?: string;
  grantedAt?: string;
}

export interface ConsentsService {
  /** Estado del consentimiento del usuario autenticado para un uso. */
  status(type: ConsentType): Promise<ConsentStatus>;
  /** Registra la decisión (otorgar o rechazar) con huella del texto leído. */
  decide(type: ConsentType, granted: boolean, language: string, textSha256: string): Promise<boolean>;
  /** Retira un consentimiento previamente otorgado. */
  revoke(type: ConsentType): Promise<boolean>;
  /** §7216 vista staff: ¿ESTE cliente otorgó el consentimiento? (VLT-308 —
   *  la IA no toca datos de un contribuyente sin su consentimiento vigente). */
  statusForClient(clientId: string, type: ConsentType): Promise<ConsentStatus>;
}

/** Huella SHA-256 del texto exacto mostrado (evidencia de qué se leyó). */
export async function sha256(text: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

// ─── Implementación real ────────────────────────────────────────────────────

const realConsents: ConsentsService = {
  async status(type) {
    const { data, error } = await supabase
      .from('consents')
      .select('granted, version, granted_at, revoked_at')
      .eq('type', type)
      .eq('version', CONSENT_VERSION)
      .is('revoked_at', null)
      .order('granted_at', { ascending: false })
      .limit(1);
    if (error) {
      console.warn('[VaulTex] consents.status:', error.message);
      return { granted: false };
    }
    const row = (data as { granted: boolean; version: string; granted_at: string }[] | null)?.[0];
    return row ? { granted: row.granted, version: row.version, grantedAt: row.granted_at } : { granted: false };
  },

  async decide(type, granted, language, textSha256) {
    const { data: auth } = await sessionUser();
    if (!auth.user) return false;
    const { data: prof } = await supabase
      .from('profiles')
      .select('organization_id')
      .eq('id', auth.user.id)
      .maybeSingle();
    const orgId = (prof as { organization_id: string } | null)?.organization_id;
    if (!orgId) return false;
    const { error } = await supabase.from('consents').insert({
      organization_id: orgId, profile_id: auth.user.id, type,
      version: CONSENT_VERSION, language, text_sha256: textSha256, granted,
    } as never);
    if (error) console.warn('[VaulTex] consents.decide:', error.message);
    return !error;
  },

  async revoke(type) {
    const { error } = await supabase
      .from('consents')
      .update({ revoked_at: new Date().toISOString() } as never)
      .eq('type', type)
      .is('revoked_at', null);
    if (error) console.warn('[VaulTex] consents.revoke:', error.message);
    return !error;
  },

  async statusForClient(clientId, type) {
    const { data: cli } = await supabase.from('clients').select('profile_id').eq('id', clientId).maybeSingle();
    const profileId = (cli as { profile_id: string | null } | null)?.profile_id;
    if (!profileId) return { granted: false };
    const { data, error } = await supabase
      .from('consents')
      .select('granted, version, granted_at')
      .eq('profile_id', profileId)
      .eq('type', type)
      .eq('version', CONSENT_VERSION)
      .is('revoked_at', null)
      .order('granted_at', { ascending: false })
      .limit(1);
    if (error) {
      console.warn('[VaulTex] consents.statusForClient:', error.message);
      return { granted: false };
    }
    const row = (data as { granted: boolean; version: string; granted_at: string }[] | null)?.[0];
    return row ? { granted: row.granted, version: row.version, grantedAt: row.granted_at } : { granted: false };
  },
};

// ─── Implementación demo ────────────────────────────────────────────────────

const demoState: Partial<Record<ConsentType, ConsentStatus>> = {};

const fixtureConsents: ConsentsService = {
  status: (type) => Promise.resolve(demoState[type] ?? { granted: false }),
  decide: (type, granted) => {
    demoState[type] = { granted, version: CONSENT_VERSION, grantedAt: new Date().toISOString() };
    return Promise.resolve(true);
  },
  revoke: (type) => {
    delete demoState[type];
    return Promise.resolve(true);
  },
  // Demo: María (c1) ya consintió; el resto de clientes no.
  statusForClient: (clientId) =>
    Promise.resolve(clientId === 'c1' ? { granted: true, version: CONSENT_VERSION } : { granted: false }),
};

export const consentsService: ConsentsService = hasBackend ? realConsents : fixtureConsents;
